import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export interface GlobalErrorNotice {
  id: string;
  title: string;
  error: string;
  onRetry?: () => void;
}

interface GlobalErrorContextValue {
  publishError: (notice: GlobalErrorNotice) => void;
  clearError: (id: string) => void;
}

const GlobalErrorContext = createContext<GlobalErrorContextValue | null>(null);

export const useGlobalError = () => useContext(GlobalErrorContext);

const GlobalErrorToast: React.FC<{
  notice: GlobalErrorNotice | null;
  onDismiss: () => void;
}> = ({ notice, onDismiss }) => {
  if (!notice) return null;

  return (
    <div className="pointer-events-none fixed inset-x-3 top-20 z-1200 flex justify-center lg:inset-x-6 lg:top-24">
      <div className="pointer-events-auto flex w-full max-w-2xl items-start gap-3 border border-rose-400/35 bg-surface px-4 py-3 text-foreground shadow-2xl">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-rose-500/15 text-rose-300">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-rose-200">{notice.title}</p>
          <p className="mt-1 wrap-break-word text-xs text-rose-100/80">{notice.error}</p>
          {notice.onRetry && (
            <button
              type="button"
              onClick={notice.onRetry}
              className="mt-2 inline-flex items-center rounded-lg border border-rose-300/40 bg-rose-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-100 transition-colors hover:bg-rose-500/20"
            >
              Try again
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-lg p-1 text-rose-200/60 transition-colors hover:bg-rose-500/10 hover:text-rose-100"
          aria-label="Dismiss error"
          title="Dismiss error"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const GlobalErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notice, setNotice] = useState<GlobalErrorNotice | null>(null);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => {
      setNotice(null);
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const publishError = useCallback((nextNotice: GlobalErrorNotice) => {
    setNotice((current) => {
      if (
        current &&
        current.id === nextNotice.id &&
        current.title === nextNotice.title &&
        current.error === nextNotice.error
      ) {
        return current;
      }
      return nextNotice;
    });
  }, []);

  const clearError = useCallback((id: string) => {
    setNotice((current) => (current?.id === id ? null : current));
  }, []);

  const value = useMemo(() => ({ publishError, clearError }), [clearError, publishError]);

  return (
    <GlobalErrorContext.Provider value={value}>
      {children}
      <GlobalErrorToast notice={notice} onDismiss={() => setNotice(null)} />
    </GlobalErrorContext.Provider>
  );
};
